import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'
import { Field } from 'factorial-form'
import { FormikProps } from 'formik'
import { UpdateAttendanceSettingMutationVariables } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import InputText from 'modules/core/components/Inputs/Text'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Return } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import Company from 'modules/core/store/models/Company'
import app from 'modules/core/store/models/app'

// TODO: using private component from another component
import ActivateCompanySlug from 'modules/app_store/components/ActivateAddonModal/ActivateCompanySlug'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

interface Props {
  attendanceSetting: AttendanceSetting
  policyForm: FormikProps<UpdateAttendanceSettingMutationVariables>
  updatePolicy: (value?: { successFlash: boolean }) => Promise<Return<void>>
}

const IdClockSharedDeviceUrl = ({ attendanceSetting, policyForm, updatePolicy }: Props) => {
  const { company } = useCurrentSession()
  const companyId = company.id

  const { running: loading, performAction: handleResetAttendanceSecretKey } = useAsyncAction(
    async () => {
      const currentCompany = new Company({ id: companyId })
      await currentCompany.resetAttendanceSecretKeyRpc()
    },
    {
      successMessage: i18n.t('time_tracking.settings.success_message_reset_url'),
      errorMessage: i18n.t('time_tracking.settings.error_message_reset_url'),
    }
  )

  const handleResetIdClockUrl = async () => {
    const data = await handleResetAttendanceSecretKey()
    if (data.success) {
      await attendanceSetting.fetch()
    }
  }

  const [slugModal, setSlugModal] = React.useState<boolean>(!company.slug)

  const hasSlugAndPublicToken = !!company.slug && !!attendanceSetting.publicToken

  const completeURL =
    company.publicUrl?.concat(
      `/employee_code_clock?token=${String(attendanceSetting.publicToken)}&setting_id=${
        attendanceSetting.id
      }`
    ) ?? ''

  const copyURL = () => {
    !!attendanceSetting.publicToken && copy(completeURL)
    app.setFlash({
      type: 'correct',
      message: i18n.t('time_tracking.settings.success_copy_url'),
    })
  }

  const disableIdSharedDevice = () => {
    policyForm.setFieldValue('idClockSharedDevice', false)
    updatePolicy()
  }

  const onSuccessSlug = async () => {
    setSlugModal(false)
    return true
  }

  const onCloseSlug = () => {
    setSlugModal(false)

    if (!company.slug) {
      disableIdSharedDevice()
    }
  }

  return (
    <>
      {hasSlugAndPublicToken && (
        <Box gap="s16">
          <InputText
            readonly
            label={i18n.t('time_tracking.settings.form.toggle.id_clock_shared_device.url')}
            field={new Field(completeURL, 'string')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t(
                  'time_tracking.settings.form.toggle.id_clock_shared_device.question'
                )}
                icon="question"
              />
            }
          />
          <Box gap="s8" flexDirection="row">
            <Button onClick={copyURL} type="secondary">
              {i18n.t('time_tracking.settings.form.toggle.id_clock_shared_device.copy_url')}
            </Button>
            <Button type="primary" disabled={loading} onClick={handleResetIdClockUrl}>
              {i18n.t('settings.customizations.attendance.generate_new_entrance_app_link')}
            </Button>
          </Box>
          <HelpCenterLink name="id_clock_shared_device">
            {i18n.t('time_tracking.settings.form.toggle.id_clock_shared_device.link')}
          </HelpCenterLink>
        </Box>
      )}
      {slugModal && <ActivateCompanySlug onSuccess={onSuccessSlug} onClose={onCloseSlug} />}
    </>
  )
}

export default observer(IdClockSharedDeviceUrl)
